import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import { Card } from "../../../components/Card";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { issbKeys, useDeleteEntity, useGetOrganizationEntities } from "../../../hooks/query";
import EditEntityModal from "./EditEntityModal";
import { Pencil, Trash, List, Users } from "@phosphor-icons/react";
import DataTableV2 from "components/DataTable/DateTableV2";
import { prettyPrintCodeName } from "utils/text-utils";
import EntityUserModal from "./EntityUserModal";
import useAirUserRoles from "../../../utils/AuthUtils";

function EntityPage() {
  const { t } = useTranslation("translation", {
    keyPrefix: "",
  });

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState();
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isEntityModalOpen, onOpen: onEntityModalOpen, onClose: onEntityModalClose } = useDisclosure();

  const { isOrgAdmin } = useAirUserRoles();

  const { isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetOrganizationEntities({
    onSuccess: (entities) => {
      setEntities(entities);
    },
    onError: (err) => handleErrors(err),
    cacheTime: 0,
    enabled: true,
  });

  const { mutate: deleteEntity } = useDeleteEntity({
    onSuccess: () => {
      showSuccessToast(toast, t("issb.entities.messages.deletedEntity"));
      queryClient.invalidateQueries(issbKeys.entity());
    },
    onError: (err) => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const handleErrors = (error) => {
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const handleAddNewEntity = () => {
    onEntityModalOpen();
  };

  const closeEntityModal = () => {
    setSelectedEntity(null);
    onEntityModalClose();
    onUserModalClose();
  };

  const handleEditEntity = (row) => {
    setSelectedEntity(row.original);
    onEntityModalOpen();
  };

  const handleManageUsers = (row) => {
    setSelectedEntity(row.original);
    onUserModalOpen();
  };

  function handleDeleteEntity(row) {
    deleteEntity(row?.original?.id);
  }

  /** @type ColumnDef[] */
  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => {
        return (
          <div style={{ cursor: "pointer" }} onClick={() => handleEditEntity(row)}>
            {row.original.name}
          </div>
        );
      },
      size: 180,
    },
    {
      header: "Sector",
      accessorFn: (row) => row.sectors?.map(prettyPrintCodeName).join(", "),
      size: 140,
    },
    {
      header: "Sub-Sector / Industry",
      accessorFn: (row) => row.subsectors?.map(prettyPrintCodeName).join(", ") || row.industry, // industry is the old version of the data
      size: 140,
    },
    {
      header: t("issb.entities.location"),
      accessorFn: (row) => row.place?.name || row.place?.countryIso || row.place?.region || row.location,
      size: 140,
    },
    {
      header: (
        <div style={{ textAlign: "center" }}>
          <b>{t("issb.questionnaire.table.headers.actions")}</b>
        </div>
      ),
      accessorKey: "action",
      size: 30,
      enableColumnFilter: false,
      isSortable: false,
      cell: (data) => (
        <Menu isLazy>
          <MenuButton as={IconButton} aria-label="Actions" icon={<List />} variant="outline" />
          <MenuList>
            <MenuItem icon={<Pencil />} onClick={() => handleEditEntity(data.row)}>
              Edit
            </MenuItem>
            <MenuItem icon={<Users size={16} />} onClick={() => handleManageUsers(data.row)}>
              Manage Users
            </MenuItem>
            {isOrgAdmin && (
              <MenuItem icon={<Trash color="red" />} onClick={() => handleDeleteEntity(data.row)}>
                {t("buttons.delete")}
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      ),
    },
  ];

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <Box marginBlockStart="5">
          <Flex marginBlockEnd="5" gap={2}>
            <Button colorScheme="customScheme" width="fit-content" onClick={handleAddNewEntity}>
              {t("buttons.add")}
            </Button>
          </Flex>

          {isEntityModalOpen && (
            <EditEntityModal entity={selectedEntity} isOpen={isEntityModalOpen} onClose={closeEntityModal} />
          )}

          {isUserModalOpen && (
            <EntityUserModal entity={selectedEntity} isOpen={isUserModalOpen} onClose={closeEntityModal} />
          )}

          <DataTableV2
            data={entities}
            columns={columns}
            isLoading={isLoadingUsers || isFetchingUsers}
            enableSorting={false}
            containerHeight="var(--chakra-sizes-2xl)"
          />
        </Box>
      </Card>
    </Page>
  );
}

export default WithAuthorization(EntityPage);
